// TODO: Overhaul — delete this file (and its consumers/tests) once the portfolio-over-time endpoint is
// reachable in the target environment (#404). The query already calls the real typed path.
import dayjs from 'dayjs';
import Decimal from 'decimal.js-light';
import { Client } from 'openapi-fetch';

import { mockDelay as delay, randomLatencyMs } from '@views/investor/queries/overhaul-mock.util';

import {
  SquirrelMoneyCoreContractsPortfolioOverTimeDto as PortfolioOverTimeDto,
  SquirrelMoneyCoreContractsPortfolioOverTimePointDto as PortfolioOverTimePointDto,
  paths as accountOverhaulPaths,
} from '@core/schemas/account-overhaul-schema';

/**
 * Typed fixture for the portfolio-over-time endpoint. Mirrors the real `PortfolioOverTimeDto`: one response
 * carries the whole series at both granularities — `monthly` (≈3 years) and `daily` (≈60 days) — and the
 * consuming chart slices it by the selected period client-side. Reached only through the flag-gated lazy
 * route (`entities/:entityId/investments-ao`), so it ships in a chunk prod never fetches. Deleted at wire-up.
 */

const NZ_DATE = 'YYYY-MM-DD';

interface MockParams {
  investmentAccountId: number | null;
  /** Force the empty/zero state (`monthly: [], daily: []`) so the chart's empty branch can be exercised. */
  empty?: boolean;
}

/** Build a plausible growing balance/interest series across `[start, end]` at the given granularity. */
function buildSeries(
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
  unit: 'day' | 'month',
  baseBalance: number,
): PortfolioOverTimePointDto[] {
  const points: PortfolioOverTimePointDto[] = [];
  // Decimal accumulation so the fabricated series stays exact (no float drift), without post-hoc rounding.
  const balanceStep = new Decimal(baseBalance).times(0.02);

  let cursor = start;
  let balance = new Decimal(baseBalance);
  let interest = new Decimal(0);

  while (!cursor.isAfter(end)) {
    balance = balance.add(balanceStep);
    interest = interest.add(balance.times(0.0004)); // small per-step accrual
    points.push({ dateNzt: cursor.format(NZ_DATE), balance: balance.toNumber(), interest: interest.toNumber() });
    cursor = cursor.add(1, unit);
  }

  return points;
}

/** Fixture factory for a `PortfolioOverTimeDto` — the full monthly + daily series. */
export function createPortfolioOverTimeMock({ investmentAccountId, empty = false }: MockParams): PortfolioOverTimeDto {
  const now = dayjs();
  // Aggregate (all-accounts) view holds a larger balance than a single account.
  const baseBalance = investmentAccountId === null ? 250_000 : 45_000;

  return {
    investmentAccountId,
    monthly: empty ? [] : buildSeries(now.subtract(35, 'month').startOf('month'), now, 'month', baseBalance),
    daily: empty ? [] : buildSeries(now.subtract(59, 'day').startOf('day'), now, 'day', baseBalance),
  };
}

/**
 * A mock `ACCOUNT_OVERHAUL_CLIENT` that serves the portfolio-over-time endpoint from the fixture above.
 * Provide it on the **narrow, lazy, flag-gated** component that owns `PortfolioOverTimeQueriesService`.
 * Entity-independent — the path `investorEntityId` is ignored; the account filter (`investmentAccountId`)
 * only scales the base balance. Every GET waits a random 500ms–1500ms to mimic fetch latency; pass
 * `delayMs` (e.g. `0`) to make it deterministic in tests.
 */
export function createPortfolioOverTimeMockClient(delayMs?: number): Client<accountOverhaulPaths> {
  return {
    async GET(_path: string, init?: { params?: { query?: { investmentAccountId?: number } } }) {
      const query = init?.params?.query ?? {};
      await delay(delayMs ?? randomLatencyMs());
      return Promise.resolve({
        data: createPortfolioOverTimeMock({ investmentAccountId: query.investmentAccountId ?? null }),
        error: undefined,
        response: new Response(null, { status: 200 }),
      });
    },
    // Cast: this stub implements only the portfolio-over-time GET, not the whole overhaul surface.
  } as unknown as Client<accountOverhaulPaths>;
}
